import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { IOfferEventDetailFragment } from 'graphql/types';
import { LinearProgress } from 'common/components/LinearProgress';
import { CertificateStatusListItem } from 'common/components/CertificateStatusListItem';
import { Chip } from 'common/components/Chip';
import { useOfferEventProgress } from 'offer/hooks/useOfferEventProgress';

interface IProps {
    offerEvent: IOfferEventDetailFragment;
}

export const ProgressSection = ({ offerEvent }: IProps) => {
    const [translate] = useTranslation();

    const { certificatePortfolioItem } = offerEvent;

    const { showScore, showProgress } = useOfferEventProgress(offerEvent);

    const { certificate, portfolioItem } = offerEvent;
    const { progress, score, scoreThreshold } = portfolioItem || {};

    const scoreValue = score || 0;

    const isExternalCertificate = !!certificate?.isExternalCertificate;
    const hasCertificate =
        isExternalCertificate || (!!certificate && !!certificatePortfolioItem);

    return (
        <>
            {(showProgress || showScore) && (
                <Stack
                    alignItems="center"
                    direction="row"
                    divider={<Divider flexItem orientation="vertical" />}
                    spacing={2}
                >
                    {showProgress && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography sx={{ mr: 2, fontSize: '1.6rem' }}>
                                {translate('progress')}
                            </Typography>

                            <LinearProgress
                                withProgressText
                                aria-label={translate('progress')}
                                color="primary"
                                sx={{ height: 8, borderRadius: 8 }}
                                value={(progress || 0) * 100}
                            />
                        </Box>
                    )}

                    {!!showScore && (
                        <Chip
                            color={
                                scoreValue >= (scoreThreshold || 0)
                                    ? 'success'
                                    : 'error'
                            }
                            label={`${Math.round(scoreValue * 100)}%`}
                            sx={{ flexShrink: 0, fontSize: '1.3rem' }}
                        />
                    )}
                </Stack>
            )}

            {!!hasCertificate && !!certificate && (
                <CertificateStatusListItem
                    certificate={certificate}
                    certificatePortfolioItem={certificatePortfolioItem}
                    isExternalCertificate={isExternalCertificate}
                    offerEventId={offerEvent.id}
                    validationStatus={
                        certificatePortfolioItem?.metadata?.validationStatus
                    }
                />
            )}
        </>
    );
};
